/**
 * MirageDocs Client-Side Encryption Module
 *
 * Security Architecture:
 * - AES-256-GCM authenticated encryption (prevents tampering)
 * - Web Crypto API for hardware-accelerated, FIPS-compliant crypto
 * - 256-bit keys generated via crypto.getRandomValues()
 * - 96-bit (12-byte) IV per encryption (NIST recommended for GCM)
 * - Keys stored in URL fragment (#) - never sent to server
 *
 * Data Format (v2):
 * "v2:<iv_base64>:<ciphertext+tag_base64>"
 *
 * Backward Compatibility:
 * - v1 (legacy): CryptoJS AES-CBC format (still supported for decryption)
 * - v2 (current): Web Crypto AES-GCM format
 *
 * @module utils/crypto
 */

import CryptoJS from 'crypto-js';

// Version prefix for AES-GCM encrypted data
const GCM_VERSION_PREFIX = 'v2:';

/**
 * Generate a cryptographically secure encryption key
 * Returns a hex string (64 chars = 32 bytes = 256 bits)
 */
export const generateEncryptionKey = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Convert hex string to Uint8Array
 */
const hexToBytes = (hex) => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
};

/**
 * Convert Uint8Array to hex string
 */
const bytesToHex = (bytes) => {
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Convert base64 to Uint8Array
 */
const base64ToBytes = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Convert Uint8Array to base64
 */
const bytesToBase64 = (bytes) => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Encrypt file using AES-256-GCM (Web Crypto API)
 * Returns: "v2:<iv_base64>:<ciphertext_base64>:<tag_base64>"
 */
export const encryptFileGCM = async (file, keyHex) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);

        // Import key
        const keyBytes = hexToBytes(keyHex);
        const cryptoKey = await crypto.subtle.importKey(
          'raw',
          keyBytes,
          { name: 'AES-GCM' },
          false,
          ['encrypt']
        );

        // Generate 12-byte IV (recommended for GCM)
        const iv = crypto.getRandomValues(new Uint8Array(12));

        // Encrypt
        const encryptedBuffer = await crypto.subtle.encrypt(
          { name: 'AES-GCM', iv: iv },
          cryptoKey,
          data
        );

        // Web Crypto appends the 16-byte auth tag to the ciphertext
        const encryptedArray = new Uint8Array(encryptedBuffer);

        // Format: v2:<iv_base64>:<encrypted_with_tag_base64>
        const result = GCM_VERSION_PREFIX +
          bytesToBase64(iv) + ':' +
          bytesToBase64(encryptedArray);

        resolve(result);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Decrypt file using AES-256-GCM (Web Crypto API)
 */
const decryptFileGCM = async (encryptedData, keyHex) => {
  // Parse format: v2:<iv_base64>:<encrypted_with_tag_base64>
  const parts = encryptedData.substring(GCM_VERSION_PREFIX.length).split(':');
  if (parts.length !== 2) {
    throw new Error('Invalid GCM encrypted data format');
  }

  const iv = base64ToBytes(parts[0]);
  const encryptedWithTag = base64ToBytes(parts[1]);

  // Import key
  const keyBytes = hexToBytes(keyHex);
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  // Decrypt (Web Crypto handles tag verification internally)
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv },
    cryptoKey,
    encryptedWithTag
  );

  return new Uint8Array(decryptedBuffer);
};

/**
 * Decrypt using legacy CryptoJS AES-CBC
 */
const decryptFileCBC = (encryptedText, key) => {
  const decrypted = CryptoJS.AES.decrypt(encryptedText, key);
  const typedArray = new Uint8Array(decrypted.sigBytes);
  for (let i = 0; i < decrypted.sigBytes; i++) {
    typedArray[i] = (decrypted.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return typedArray;
};

/**
 * Universal decrypt function - handles both GCM (v2) and legacy CBC
 */
export const decryptFile = async (encryptedData, key) => {
  const trimmedData = encryptedData.trim();

  if (trimmedData.startsWith(GCM_VERSION_PREFIX)) {
    // New AES-GCM format
    return await decryptFileGCM(trimmedData, key);
  } else {
    // Legacy CryptoJS AES-CBC format
    return decryptFileCBC(trimmedData, key);
  }
};

/**
 * Encrypt file - uses AES-GCM for new encryptions
 */
export const encryptFile = async (file, key) => {
  return await encryptFileGCM(file, key);
};
